import React from 'react';
import {
  PageLayout,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Switch,
  ThemeSwitcher,
} from '@lognai/synapse-design-system-v2';
import { useAuth } from '../hooks/useAuth';
import {
  usePreferences,
  useUpdatePreferences,
  useUpdateProfile,
} from '../hooks/useAccountSettings';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { TwoFactorCard } from '../components/security/TwoFactorCard';
import { CompanyColorPicker } from '../components/CompanyColorPicker';
import { DensityPicker } from '../components/DensityPicker';
import { EmailMutesCard } from '../components/EmailMutesCard';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value || '—'}</span>
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const { user, company } = useAuth();

  // Profile
  const updateProfile = useUpdateProfile();
  const [fullName, setFullName] = React.useState(user?.fullName ?? '');
  // The name arrives with the session, which may resolve after the first render.
  React.useEffect(() => {
    setFullName((current) => (current === '' ? user?.fullName ?? '' : current));
  }, [user?.fullName]);
  const nameChanged = fullName.trim() !== (user?.fullName ?? '').trim();

  // Notifications
  const prefsQuery = usePreferences();
  const updatePrefs = useUpdatePreferences();
  const emailEnabled = prefsQuery.data?.notifications?.email !== false;

  return (
    <PageLayout className="animate-fade-in-up">
      <h1 className="text-2xl font-semibold mb-4">Settings</h1>

      <div className="flex flex-col gap-4 max-w-2xl">
        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Your account details.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Editable, unlike the rest: a name is the user's own, and there
                was no way to correct a typo in it anywhere in the product —
                only an administrator could (QA ledger AUTH-011). Email, roles
                and company stay read-only: they are identity and
                authorisation, and changing them belongs to an admin flow. */}
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="full-name" className="text-xs text-muted-foreground">
                Full name
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="full-name"
                  className="max-w-xs"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  disabled={updateProfile.isPending}
                />
                <Button
                  variant="outline"
                  disabled={!nameChanged || fullName.trim().length === 0 || updateProfile.isPending}
                  onClick={() => updateProfile.mutate(fullName.trim())}
                >
                  {updateProfile.isPending ? 'Saving…' : 'Save'}
                </Button>
                {nameChanged && !updateProfile.isPending && (
                  <Button variant="ghost" onClick={() => setFullName(user?.fullName ?? '')}>
                    Cancel
                  </Button>
                )}
              </div>
              {updateProfile.isError && (
                <p role="alert" className="text-sm text-destructive">
                  {(updateProfile.error as Error)?.message || 'Could not update your name'}
                </p>
              )}
              {/* Not gated on `!nameChanged`: the session refetch that clears
                  that flag can land after the save, which would swallow the
                  confirmation exactly when it is due. */}
              {updateProfile.isSuccess && !updateProfile.isPending && (
                <p className="text-sm text-success-foreground">Name updated.</p>
              )}
            </div>
            <Field label="Email" value={user?.email} />
            <Field label="Roles" value={user?.roles?.join(', ')} />
            {company?.name && <Field label="Company" value={company.name} />}
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>Change your password.</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Was an inline copy of the account menu's modal — same mutation,
                duplicated fields and validation, and rules that had already
                diverged (QA ledger AUTH-009). */}
            <ChangePasswordForm idPrefix="settings" />
          </CardContent>
        </Card>

        {/* Two-factor. Its own card rather than a row inside Security: enrolling
            is a multi-step flow with a QR code and a set of backup codes to
            save, and none of that fits beside a password form. */}
        <TwoFactorCard />

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Theme and row density are yours; the primary color is set per company.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Theme</p>
              <ThemeSwitcher />
            </div>
            <CompanyColorPicker />
            <DensityPicker />
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>Manage how we contact you.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">Email notifications</span>
                <span className="text-xs text-muted-foreground">
                  Receive notification emails (the only channel currently delivered).
                </span>
                {prefsQuery.isError && (
                  <span className="text-xs text-destructive">
                    Couldn’t load preferences; showing defaults.
                  </span>
                )}
                {updatePrefs.isError && (
                  <span className="text-xs text-destructive">
                    Failed to save preference. Please try again.
                  </span>
                )}
              </div>
              <Switch
                checked={emailEnabled}
                disabled={prefsQuery.isLoading || updatePrefs.isPending}
                onCheckedChange={(checked) =>
                  updatePrefs.mutate({ notifications: { email: checked } })
                }
                aria-label="Email notifications"
              />
            </div>
          </CardContent>
        </Card>

        {/* Per-mail personal opt-outs, under the master switch above. */}
        <EmailMutesCard />
      </div>
    </PageLayout>
  );
};

export default SettingsPage;
